// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { DocumentViewer, FileBrowser } from '@/components/advanced/documents';

export default function DocumentsPage() {
  // Inline data-URI image so the viewer renders with no network calls
  const mockImageSrc =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420">
        <rect width="100%" height="100%" fill="#e7f5ff"/>
        <rect x="40" y="40" width="560" height="340" rx="12" fill="#ffffff" stroke="#74c0fc" stroke-width="2"/>
        <text x="320" y="200" font-family="sans-serif" font-size="28" fill="#1971c2" text-anchor="middle">Q1 Financial Report.pdf</text>
        <text x="320" y="240" font-family="sans-serif" font-size="16" fill="#868e96" text-anchor="middle">Inline preview (mock)</text>
      </svg>`
    );

  // Mock file system for the FileBrowser
  const mockFiles = [
    {
      id: 'f1',
      name: 'Financial Reports',
      type: 'folder',
      modifiedAt: new Date('2024-01-15'),
      shared: true,
    },
    {
      id: 'f2',
      name: 'Marketing Assets',
      type: 'folder',
      modifiedAt: new Date('2024-02-02'),
    },
    {
      id: 'd1',
      name: 'Q1 Report.pdf',
      type: 'file',
      mimeType: 'application/pdf',
      size: 2_457_600,
      modifiedAt: new Date('2024-03-10'),
      starred: true,
    },
    {
      id: 'd2',
      name: 'Budget.xlsx',
      type: 'file',
      mimeType: 'application/vnd.ms-excel',
      size: 845_000,
      modifiedAt: new Date('2024-03-12'),
    },
    {
      id: 'd3',
      name: 'Logo.png',
      type: 'file',
      mimeType: 'image/png',
      size: 128_400,
      modifiedAt: new Date('2024-02-20'),
      shared: true,
    },
    {
      id: 'd4',
      name: 'Intro.mp4',
      type: 'file',
      mimeType: 'video/mp4',
      size: 18_900_000,
      modifiedAt: new Date('2024-01-30'),
    },
  ];

  const noop = () => {};

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Document Components</Title>
        <Text c="dimmed" size="sm">
          Components for previewing files and browsing document libraries with familiar
          toolbar, metadata, and grid/list views
        </Text>
      </Stack>

      {/* DocumentViewer */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DocumentViewer</Title>
            <Text size="sm" c="dimmed">
              Inline previewer with toolbar (zoom, rotate, download, share) and an optional
              metadata footer. Supports pdf, image, video, and a graceful fallback for
              unsupported types.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <DocumentViewer
              src={mockImageSrc}
              type="image"
              title="Q1 Financial Report.pdf"
              metadata={{
                size: '2.4 MB',
                author: 'Oscar Martinez',
                createdAt: new Date('2024-01-10'),
                modifiedAt: new Date('2024-03-10'),
                version: 'v1.3',
              }}
              onDownload={noop}
              onPrint={noop}
              onShare={noop}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<DocumentViewer
  src="/files/q1-report.pdf"
  type="image"
  title="Q1 Financial Report.pdf"
  metadata={{
    size: '2.4 MB',
    author: 'Oscar Martinez',
    createdAt: new Date('2024-01-10'),
    modifiedAt: new Date('2024-03-10'),
    version: 'v1.3',
  }}
  onDownload={() => downloadFile(id)}
  onPrint={() => window.print()}
  onShare={() => openShareModal(id)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • src - URL or data-URI of the document (required)
              <br />
              • type - 'pdf' | 'image' | 'video' | 'spreadsheet' | 'presentation' | 'text' | 'unknown' (required)
              <br />
              • title - Header label shown in the toolbar
              <br />
              • metadata - size / pages / author / version / dates footer
              <br />
              • showToolbar, showMetadata - toggle chrome (default true)
              <br />
              • allowZoom, allowRotate, allowFullscreen - toolbar capabilities
              <br />
              • totalPages, currentPage, onPageChange - page navigation
              <br />
              • onDownload, onPrint, onShare, onEdit, onDelete, onOpenExternal - action handlers
              <br />
              • fallback - custom node for unsupported types
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* DocumentViewer - fallback */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DocumentViewer — Fallback State</Title>
            <Text size="sm" c="dimmed">
              When the file type cannot be rendered inline, the viewer shows a "Preview not
              available" state with a download affordance.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <DocumentViewer
              src="/files/budget.xlsx"
              type="spreadsheet"
              title="Annual Budget.xlsx"
              metadata={{ size: '845 KB', author: 'Angela Martin' }}
              onDownload={noop}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<DocumentViewer
  src="/files/budget.xlsx"
  type="spreadsheet"
  title="Annual Budget.xlsx"
  metadata={{ size: '845 KB', author: 'Angela Martin' }}
  onDownload={() => downloadFile(id)}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* FileBrowser - grid */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FileBrowser</Title>
            <Text size="sm" c="dimmed">
              Full file manager with upload / new-folder actions, search, breadcrumb
              navigation, multi-select, per-item context menus, and a grid/list view toggle.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (grid view):</Text>
            <FileBrowser
              files={mockFiles}
              currentPath={['Documents']}
              viewMode="grid"
              onNavigate={noop}
              onFileClick={noop}
              onFolderClick={noop}
              onFileSelect={noop}
              onUpload={noop}
              onNewFolder={noop}
              onSearchChange={noop}
              onViewModeChange={noop}
              onDownload={noop}
              onShare={noop}
              onStar={noop}
              onRename={noop}
              onDelete={noop}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const files = [
  { id: 'f1', name: 'Financial Reports', type: 'folder', shared: true },
  {
    id: 'd1',
    name: 'Q1 Report.pdf',
    type: 'file',
    mimeType: 'application/pdf',
    size: 2_457_600,
    modifiedAt: new Date('2024-03-10'),
    starred: true,
  },
  // ... more files
];

<FileBrowser
  files={files}
  currentPath={['Documents']}
  viewMode="grid"
  onNavigate={(path) => setPath(path)}
  onFolderClick={(folder) => openFolder(folder)}
  onFileClick={(file) => previewFile(file)}
  onUpload={() => openUploadDialog()}
  onNewFolder={() => createFolder()}
  onDownload={(files) => downloadAll(files)}
  onDelete={(files) => deleteAll(files)}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* FileBrowser - list */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FileBrowser — List View</Title>
            <Text size="sm" c="dimmed">
              The same data rendered as a sortable list with size and modified columns and a
              select-all header checkbox.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (list view):</Text>
            <FileBrowser
              files={mockFiles}
              viewMode="list"
              onFileClick={noop}
              onFolderClick={noop}
              onFileSelect={noop}
              onDownload={noop}
              onShare={noop}
              onStar={noop}
              onRename={noop}
              onCopy={noop}
              onCut={noop}
              onDelete={noop}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<FileBrowser files={files} viewMode="list" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>FileItem Interface:</Text>
            <Code block language="typescript">
{`interface FileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  mimeType?: string;
  size?: number;
  createdAt?: Date;
  modifiedAt?: Date;
  starred?: boolean;
  shared?: boolean;
  path?: string;
  thumbnail?: string;
  metadata?: Record<string, unknown>;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Document Library</Text>
              <Text size="xs" c="dimmed">
                Use FileBrowser as a shared drive for policies, contracts, and assets with
                folders, search, and starring.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Inline Previews</Text>
              <Text size="xs" c="dimmed">
                Use DocumentViewer to preview PDFs, images, and videos without leaving the app,
                falling back to download for other types.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Review &amp; Approvals</Text>
              <Text size="xs" c="dimmed">
                Pair the viewer's metadata footer (author, version, dates) with action handlers
                for share and edit during document review.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
